import React, { useState } from "react";
import axios from "axios";
import { Form, Button } from "react-bootstrap";
import { FaDownload , FaSyncAlt} from "react-icons/fa";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import * as bpmnDefinitions from "../../../constants/bpmnDefinitions";
import { generateCustomizedSchema } from "../../../services/generateCustomizedSchema";
import { storageKeys } from "../../../constants/storageKeys";
import { downloadFile } from "../../../utils/fileUtils";
import initialSettings from "../../../constants/settings.json";
 
// Define the schema properties that can be toggled
const schemaProperties = [
  { key: bpmnDefinitions.MULTIPLE_POOLS, label: "Multiple Pools" },
  { key: bpmnDefinitions.LOOP, label: "Do-While Loops" },
  { key: bpmnDefinitions.EXCLUSIVE_GATEWAY, label: "Exclusive Gateways" },
  { key: bpmnDefinitions.PARALLEL_GATEWAY, label: "Parallel Gateways" },
  { key: bpmnDefinitions.EVENT_BASED_GATEWAY, label: "Event Based Gateways" },
  { key: bpmnDefinitions.MESSAGE_SEND_EVENT, label: "Message Throw Events" },
  { key: bpmnDefinitions.MESSAGE_RECEIVE_EVENT, label: "Message Catch Events" },
  { key: bpmnDefinitions.TIMER_EVENT, label: "Timer Events" },
  { key: bpmnDefinitions.USER_TASK, label: "User Tasks" },
  { key: bpmnDefinitions.MANUAL_TASK, label: "Manual Tasks" },
  { key: bpmnDefinitions.SERVICE_TASK, label: "Service Tasks" },
  { key: bpmnDefinitions.SCRIPT_TASK, label: "Script Tasks" },
  { key: bpmnDefinitions.BUSINESS_RULE_TASK, label: "Business Rule Tasks" },
  { key: bpmnDefinitions.SUBPROCESS, label: "Collapsed Subprocesses" },
  { key: bpmnDefinitions.DATA_OBJECT, label: "Data Objects" },
  { key: bpmnDefinitions.DATA_STORE, label: "Data Stores" },
];

const SchemaSettings = () => {

  const [loading, setLoading] = useState(false);
    
  // Initial selectedProperties
  const initialSelectedProperties = schemaProperties.reduce((acc, property) => {
    acc[property.key] = true;
    return acc;
  }, {});

  const [selectedProperties, setSelectedProperties] = useLocalStorage(
    storageKeys.SCHEMA_PROPERTIES,
    initialSelectedProperties
  );

  const [bpmnTranslatorServiceUrl, setBpmnTranslatorServiceUrl] = useLocalStorage(
    storageKeys.BPMN_TRANSLATOR_SERVICE_URL,
    JSON.parse(initialSettings.localStorage.BPMN_TRANSLATOR_SERVICE_URL)
  );  

  const [llmJsonSchemaServiceUrl, setLlmJsonSchemaServiceUrl] = useLocalStorage(
    storageKeys.LLM_JSON_SCHEMA_SERVICE_URL,
    JSON.parse(initialSettings.localStorage.LLM_JSON_SCHEMA_SERVICE_URL)
  );

  const [llmJsonSchema, setLlmJsonSchema] = useLocalStorage(
    storageKeys.LLM_JSON_SCHEMA,
    JSON.parse(initialSettings.localStorage.LLM_JSON_SCHEMA)
  );

  /**
   * Handles changes to the schema property checkboxes.
   * 
   * - Updates the selectedProperties state when a checkbox is toggled.
   * - If the "Event Based Gateways" checkbox is checked, automatically selects all event-related checkboxes.
   * - If any event-related checkbox is toggled and all event checkboxes become unchecked, 
   *   automatically unchecks "Event Based Gateways".
   * 
   * @param {object} e - The change event from the checkbox input.
   */
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    // Create a copy of the current selected properties and update the toggled property
    const updatedProperties = { ...selectedProperties, [name]: checked };

    // Get all event-related property keys
    const eventKeys = bpmnDefinitions.ALL_EVENTS;

    // If "Event Based Gateways" is checked, select all event-related checkboxes
    if (name === bpmnDefinitions.EVENT_BASED_GATEWAY && checked) {
      eventKeys.forEach((eventKey) => (updatedProperties[eventKey] = true));
    }

    // If an event-related checkbox is toggled
    if (eventKeys.includes(name)) {
      // Check if all event-related checkboxes are now unchecked
      const allDeselected = eventKeys.every(
        (eventKey) => !updatedProperties[eventKey]
      );

      // If all event-related checkboxes are unchecked, also uncheck "Event Based Gateways"
      if (allDeselected) {
        updatedProperties[bpmnDefinitions.EVENT_BASED_GATEWAY] = false;
      }
    }

    // Update the selected properties in local storage and state
    setSelectedProperties(updatedProperties);
  };

  const handleSelectAllChange = (e) => {
    const { checked } = e.target;
    const updatedProperties = schemaProperties.reduce((acc, property) => {
      acc[property.key] = checked;
      return acc;
    }, {});
    setSelectedProperties(updatedProperties);
  };

  const handleDownload = async () => {
    if (llmJsonSchema) {
      downloadFile(llmJsonSchema, "llm-schema.json");
    } 
    else {
      const customizedSchema = generateCustomizedSchema();
      downloadFile(customizedSchema, "custom-schema.json");
    }
  };

  const handleFetchSchema = async () => {
    setLoading(true);
    try {
      const response = await axios.get(llmJsonSchemaServiceUrl, { responseType: 'json' });
      setLlmJsonSchema(response.data);
    } catch (error) {
      alert("Failed to fetch schema from service: " + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeLlmJsonSchemaServiceUrl = (value) => {
    setLlmJsonSchemaServiceUrl(value);
    setLlmJsonSchema(null);
  };


  return (
    <Form>

      {/* Field to change BPMN Translator Service URL */}
      <Form.Group controlId="bpmn-translator-service-url" className="mb-3">
        <Form.Label>BPMN Translator Service URL</Form.Label>
        <Form.Control
          type="text"
          value={bpmnTranslatorServiceUrl}
          placeholder="Enter translator service URL"
          onChange={(e) => {
            setBpmnTranslatorServiceUrl(e.target.value);
            // Optionally trigger re-render or add further logic as needed
          }}
        />
        <Form.Text className="text-muted">
          Service for translating LLM JSON model to BPMN XML.
        </Form.Text>
      </Form.Group>

      <div
          className="d-flex flex-wrap align-items-end gap-2"
          style={{ rowGap: 12 }}
        >
          <Form.Group
            className="flex-grow-1"
            style={{ minWidth: 220, marginBottom: 0, flexBasis: "250px" }}
          >
        <Form.Label>LLM JSON Schema Service URL</Form.Label>
        <Form.Control
          type="text"
          value={llmJsonSchemaServiceUrl}
          placeholder="Enter schema service URL"
          onChange={(e) => {
            handleChangeLlmJsonSchemaServiceUrl(e.target.value);
          }}
        />
          </Form.Group>
          <div style={{ flexShrink: 0 }}>
            <Button
              size="sm"
              className="mb-1 w-100"
              onClick={handleFetchSchema}
              disabled={loading || !llmJsonSchemaServiceUrl}
              variant="primary"
              style={{ whiteSpace: "nowrap", minWidth: 120 }}
            >
              <FaSyncAlt className="me-1" />
              {loading ? "Fetching..." : "Fetch Schema"}
            </Button>
          </div>
        </div>
        <Form.Text className="text-muted">
          Service for getting the LLM JSON model instead of the local one.
        </Form.Text>


      <div className="mb-2"/>
      {!llmJsonSchema && (
        <>
        <Form.Label className="mb-0 mt-3">
        Supported Elements in Local JSON Schema:
        </Form.Label>
        <br />
        <Form.Text className="text-muted">
          These options determine which elements the LLM is allowed to use in the local JSON schema.
        </Form.Text>
          <Form.Group controlId="select-all">
            <Form.Check
              type="checkbox"
              label="Select All"
              checked={Object.values(selectedProperties).every((value) => value)}
              onChange={handleSelectAllChange}
            />
          </Form.Group>

          <div className="ms-4 mt-3">
            {schemaProperties.map((property) => (
              <Form.Group controlId={property.key} key={property.key}>
                <Form.Check
                  type="checkbox"
                  label={<span>{property.label}</span>}
                  name={property.key}
                  checked={selectedProperties[property.key]}
                  onChange={handleCheckboxChange}
                />
              </Form.Group>
            ))}
          </div>
        </>
      )}

      <Button size="sm" className="mt-4" onClick={handleDownload}>
        Download Schema <FaDownload />
      </Button>
    </Form>
  );
};

export default SchemaSettings;
